/**
 * Authentication utilities for Music Event Platform
 * All API endpoints are protected with Bearer token authentication.
 * The `auth` client handles login/signup and token management.
 */

import { api, tokenStorage } from "./api";

// =====================
// Types
// =====================

export interface User {
	id: string;
	email: string;
	name: string;
	firstName?: string;
	lastName?: string;
	avatar?: string;
	role: "admin" | "manager" | "artist" | "venue";
	plan?: "free" | "pro" | "enterprise";
	createdAt: string;
	updatedAt: string;
}

export interface AuthTokens {
	accessToken: string;
	refreshToken: string;
	expiresIn?: number;
}

export interface LoginCredentials {
	email: string;
	password: string;
	rememberMe?: boolean;
}

export interface RegisterData {
	name: string;
	email: string;
	password: string;
	confirmPassword: string;
	acceptTerms: boolean;
}

// =====================
// Token Management
// =====================

const USER_KEY = "user";

/**
 * Store user data in localStorage
 */
export function storeUser(user: User): void {
	localStorage.setItem(USER_KEY, JSON.stringify(user));
}

/**
 * Get stored user data
 */
export function getStoredUser(): User | null {
	const userData = localStorage.getItem(USER_KEY);
	if (!userData) return null;

	try {
		return JSON.parse(userData);
	} catch {
		return null;
	}
}

/**
 * Clear stored user data
 */
export function clearStoredUser(): void {
	localStorage.removeItem(USER_KEY);
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
	return !!tokenStorage.getAccessToken();
}

/**
 * Get time until token expires
 */
export function getTokenTimeRemaining(): number | null {
	// This would need to be implemented based on token payload
	// For now, assume token is valid if it exists
	const token = tokenStorage.getAccessToken();
	if (!token) return null;

	// In production, decode JWT and check exp claim
	return Infinity;
}

// =====================
// Auth Functions
// =====================

/**
 * Parse JWT token (without verification - for client-side only)
 */
export function parseJwt(token: string): Record<string, unknown> | null {
	try {
		const base64Url = token.split(".")[1];
		const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
		const jsonPayload = decodeURIComponent(
			atob(base64)
				.split("")
				.map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
				.join("")
		);
		return JSON.parse(jsonPayload);
	} catch {
		return null;
	}
}

/**
 * Check if token is expired
 */
export function isTokenExpired(token: string): boolean {
	try {
		const payload = parseJwt(token);
		if (!payload || !payload.exp) return true;

		const expirationTime = (payload.exp as number) * 1000;
		return Date.now() >= expirationTime;
	} catch {
		return true;
	}
}

/**
 * Get time until token refresh is needed (5 minutes before expiry)
 */
export function getRefreshThreshold(): number {
	return 5 * 60 * 1000; // 5 minutes in milliseconds
}

// =====================
// Password Utilities
// =====================

/**
 * Calculate password strength score
 */
export function calculatePasswordStrength(password: string): {
	score: number;
	label: string;
	color: string;
	requirements: {
		length: boolean;
		uppercase: boolean;
		lowercase: boolean;
		number: boolean;
		special: boolean;
	};
} {
	const requirements = {
		length: password.length >= 8,
		uppercase: /[A-Z]/.test(password),
		lowercase: /[a-z]/.test(password),
		number: /[0-9]/.test(password),
		special: /[^A-Za-z0-9]/.test(password),
	};

	const score = Object.values(requirements).filter(Boolean).length;

	const labels = ["Very Weak", "Weak", "Fair", "Good", "Strong"];
	const colors = [
		"bg-red-500",
		"bg-red-500",
		"bg-orange-500",
		"bg-yellow-500",
		"bg-green-500",
	];

	return {
		score,
		label: labels[score - 1] || labels[0],
		color: colors[score - 1] || colors[0],
		requirements,
	};
}

/**
 * Check if password meets minimum requirements
 */
export function isPasswordValid(password: string): boolean {
	return (
		password.length >= 8 &&
		/[A-Z]/.test(password) &&
		/[a-z]/.test(password) &&
		/[0-9]/.test(password)
	);
}

// =====================
// Session Management
// =====================

/**
 * Clear all auth-related data
 */
export function clearAuthData(): void {
	tokenStorage.clearTokens();
	clearStoredUser();
}

/**
 * Initialize auth state from stored data
 */
export function initAuthState(): { user: User | null; isAuthenticated: boolean } {
	const user = getStoredUser();
	const isAuth = isAuthenticated();

	return { user, isAuthenticated: isAuth };
}

// =====================
// Role Permissions
// =====================

export type Permission =
	| "view_events"
	| "create_events"
	| "edit_events"
	| "delete_events"
	| "view_tours"
	| "create_tours"
	| "edit_tours"
	| "delete_tours"
	| "manage_team"
	| "manage_venues"
	| "view_analytics"
	| "manage_billing"
	| "admin_access";

const ROLE_PERMISSIONS: Record<User["role"], Permission[]> = {
	admin: [
		"view_events",
		"create_events",
		"edit_events",
		"delete_events",
		"view_tours",
		"create_tours",
		"edit_tours",
		"delete_tours",
		"manage_team",
		"manage_venues",
		"view_analytics",
		"manage_billing",
		"admin_access",
	],
	manager: [
		"view_events",
		"create_events",
		"edit_events",
		"delete_events",
		"view_tours",
		"create_tours",
		"edit_tours",
		"delete_tours",
		"manage_team",
		"manage_venues",
		"view_analytics",
	],
	artist: ["view_events", "create_events", "edit_events", "view_tours"],
	venue: ["view_events", "manage_venues"],
};

/**
 * Check if user has specific permission
 */
export function hasPermission(userRole: User["role"], permission: Permission): boolean {
	return ROLE_PERMISSIONS[userRole]?.includes(permission) ?? false;
}

/**
 * Check if user has any of the specified permissions
 */
export function hasAnyPermission(
	userRole: User["role"],
	permissions: Permission[]
): boolean {
	return permissions.some((p) => hasPermission(userRole, p));
}

/**
 * Check if user has all specified permissions
 */
export function hasAllPermissions(
	userRole: User["role"],
	permissions: Permission[]
): boolean {
	return permissions.every((p) => hasPermission(userRole, p));
}

// =====================
// Auth Client (for components using auth.getToken(), auth.signIn(), etc.)
// =====================

interface AuthResponse {
	success: boolean;
	error?: string;
	message?: string;
	user?: any;
}

/**
 * Auth client object providing token management and auth API calls.
 * Used by AuthModal, VATContext, ChatBadge, and other components.
 */
export const auth = {
	/**
	 * Get the current access token
	 */
	getToken(): string | null {
		return tokenStorage.getAccessToken();
	},

	/**
	 * Sign in with email and password.
	 * Protected endpoint - uses Bearer token after successful login.
	 */
	async signIn(email: string, password: string): Promise<AuthResponse> {
		try {
			const response = await api.post<{
				accessToken: string;
				refreshToken: string;
				user: any;
			}>("/auth/login", { email, password });

			if (response.error) {
				return { success: false, error: response.error.message };
			}

			if (response.data) {
				tokenStorage.setAccessToken(response.data.accessToken);
				tokenStorage.setRefreshToken(response.data.refreshToken);
				storeUser(response.data.user);
				return { success: true, message: "Signed in successfully", user: response.data.user };
			}

			return { success: false, error: "Invalid response from server" };
		} catch (err: any) {
			return { success: false, error: err.message || "Failed to sign in" };
		}
	},

	/**
	 * Sign up a new user.
	 */
	async signUp(
		email: string,
		password: string,
		options?: { full_name?: string }
	): Promise<AuthResponse> {
		try {
			const response = await api.post<{ message: string }>("/auth/register", {
				email,
				password,
				name: options?.full_name,
			});

			if (response.error) {
				return { success: false, error: response.error.message };
			}

			return {
				success: true,
				message: response.data?.message || "Account created! Please check your email to verify.",
			};
		} catch (err: any) {
			return { success: false, error: err.message || "Failed to sign up" };
		}
	},
};

/**
 * Re-export tokenStorage for components importing from @/lib/auth
 */
export { tokenStorage };
